import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, ButtonBase, IconButton, Skeleton, Tooltip, Typography, useTheme } from "@mui/material";
import { ChevronLeftRounded, DeleteOutlineRounded, EditOutlined, PlayArrowRounded, WhatsApp } from "@mui/icons-material";
import { format } from "date-fns";

import PageTitle from "../../../components/PageTitle";
import { EmptyState, PatientHexAvatar, TagChip, useGatedNavigate } from "../../../components/ui";
import { SimpleSnackbar } from "../../../components/snackbar";
import { MessageDialog } from "../../../components/message-dialog";
import { handlePromise } from "../../../utils/handlePromisse";
import { getPatientNameById, getPatientsPaginated2, Patient, PatientEvent, deletePatient } from "../../../services/patient.service";
import { PatientForm } from "./form/patient-form";
import { savePatientData } from "./form/save-patient";
import { lastConsultationDate, whatsappUrl } from "./patient-format";
import { applyMask } from "../../../utils";

// ----------------------------------------------------------------------

const phoneMask = applyMask("phone");

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : format(date, "dd/MM/yyyy");
};

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
}

function SectionCard({ title, children }: SectionCardProps) {
  const theme = useTheme();
  return (
    <Box sx={{ backgroundColor: "background.paper", border: `1px solid ${theme.palette.divider}`, borderRadius: "18px", p: 3 }}>
      <Typography sx={{ mb: 1.5, fontFamily: theme.typography.fontFamilyDisplay, fontSize: 21, color: "text.primary" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function EventTimeline({ events }: { events: PatientEvent[] }) {
  const theme = useTheme();
  const sorted = [...events].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return (
    <Box sx={{ position: "relative", pl: "28px" }}>
      <Box sx={{ position: "absolute", left: 8, top: 4, bottom: 4, width: "2px", backgroundColor: theme.palette.brand.heroBorder }} />
      {sorted.map((event, index) => (
        <Box key={`${event.date}-${index}`} sx={{ position: "relative", mb: "18px" }}>
          <Box sx={{ position: "absolute", left: -28, top: 2, width: 18, height: 18, borderRadius: "50%", backgroundColor: "background.paper", border: `3px solid ${theme.palette.primary.main}` }} />
          <Typography sx={{ mb: "5px", fontSize: "12px", color: "text.secondary" }}>{formatDate(event.date)}</Typography>
          <Box sx={{ backgroundColor: theme.palette.brand.surface2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: "13px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>{event.type}</Typography>
            {event.description && (
              <Typography sx={{ mt: "4px", fontSize: "13px", lineHeight: 1.5, color: theme.palette.brand.heroText }}>{event.description}</Typography>
            )}
            {(event.images?.length ?? 0) > 0 && (
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {event.images!.map((img) => (
                  <Box key={img} component="img" src={img} alt="" sx={{ width: 60, height: 60, borderRadius: "8px", objectFit: "cover" }} />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

type DetailPatient = Patient & { images?: Array<{ id: number; imageUrl: string }> };

export const PatientDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { navigateGated, gateDialogs } = useGatedNavigate();

  const [patient, setPatient] = useState<DetailPatient | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [allTags, setAllTags] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [result, error] = await handlePromise(getPatientNameById(Number(id)));
    setLoading(false);
    if (error) {
      setSnackbar("Erro ao carregar o paciente.");
      return;
    }
    setPatient({ ...result.data, id: Number(id) });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(async (patientData: Patient & { photos?: File[]; removedImageIds?: number[] }) => {
    setSnackbar("");
    setSaving(true);
    const [_, error] = await savePatientData(patientData);
    setSaving(false);
    if (error) {
      setSnackbar(error.response?.data?.message || "Erro ao salvar paciente.");
      return;
    }
    setEditOpen(false);
    setSnackbar("Paciente atualizado com sucesso!");
    await load();
  }, [load]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    const [_, error] = await handlePromise(deletePatient(Number(id)));
    setDeleting(false);
    if (error) {
      setSnackbar(error.response?.data?.message || "Erro ao deletar paciente.");
      return;
    }
    setDeleteOpen(false);
    navigate("/dashboard/patients");
  }, [id, navigate]);

  const handleOpenEdit = useCallback(async () => {
    setSnackbar("");
    setEditOpen(true);
    if (allTags !== null) return;
    const [result, error] = await handlePromise(getPatientsPaginated2({ page: 1, size: 1000 }));
    if (error) return;
    const patients: Patient[] = result.data.data ?? [];
    const set = new Set<string>();
    patients.forEach((p) => {
      if (Array.isArray(p.tags)) p.tags.forEach((t) => set.add(t));
    });
    setAllTags(Array.from(set));
  }, [allTags]);

  const emptyText = (text?: string) =>
    text ? (
      <Typography sx={{ fontSize: "14px", lineHeight: 1.7, color: "secondary.main", whiteSpace: "pre-wrap" }}>{text}</Typography>
    ) : (
      <Typography sx={{ fontSize: "13px", color: "text.disabled" }}>Nada registrado ainda.</Typography>
    );

  return (
    <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
      <PageTitle title="Paciente" />
      {gateDialogs}
      <SimpleSnackbar show={!!snackbar} message={snackbar} onDismiss={() => setSnackbar("")} />

      <ButtonBase onClick={() => navigate("/dashboard/patients")} sx={{ mb: 2, gap: "7px", fontSize: "13.5px", fontWeight: 600, color: "text.secondary", fontFamily: theme.typography.fontFamily }}>
        <ChevronLeftRounded sx={{ fontSize: 17 }} /> Voltar para pacientes
      </ButtonBase>

      {loading && <Skeleton variant="rounded" height={140} sx={{ borderRadius: "18px", mb: 3 }} />}

      {!loading && !patient && <EmptyState title="Paciente não encontrado" actionLabel="Voltar" onAction={() => navigate("/dashboard/patients")} />}

      {!loading && patient && (
        <>
          <Box sx={{ backgroundColor: "background.paper", border: `1px solid ${theme.palette.divider}`, borderRadius: "18px", p: "26px", mb: 3, display: "flex", alignItems: "center", gap: "22px", flexWrap: "wrap" }}>
            <PatientHexAvatar name={patient.name} size={70} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: { xs: 21, md: 30 }, color: "text.primary" }}>
                {patient.name}
              </Typography>
              {(patient.tags?.length ?? 0) > 0 && (
                <Box sx={{ mt: "6px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {patient.tags!.map((tag) => (
                    <TagChip key={tag} label={tag} />
                  ))}
                </Box>
              )}
              {patient.phone && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "13px", color: theme.palette.brand.heroText }}>{phoneMask.onValue(patient.phone)}</Typography>
                  {whatsappUrl(patient.phone) && (
                    <Tooltip title="Abrir conversa no WhatsApp" placement="top">
                      <IconButton
                        size="small"
                        component="a"
                        href={whatsappUrl(patient.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Abrir conversa no WhatsApp"
                        sx={{ color: "#25D366", p: 0.25 }}
                      >
                        <WhatsApp sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
              <Tooltip title="Excluir paciente" placement="top">
                <IconButton
                  aria-label="Excluir paciente"
                  onClick={() => setDeleteOpen(true)}
                  sx={{ width: 46, height: 46, border: `1.5px solid ${theme.palette.error.main}`, color: "error.main", borderRadius: "10px" }}
                >
                  <DeleteOutlineRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" onClick={handleOpenEdit} startIcon={<EditOutlined />} sx={{ height: 46, px: 2, border: `1.5px solid ${theme.palette.primary.main}`, color: "primary.main", fontWeight: 600 }}>
                Editar
              </Button>
              <Button variant="contained" onClick={() => navigateGated("/dashboard/query", { patientId: patient.id })} startIcon={<PlayArrowRounded />} sx={{ height: 46, px: 2 }}>
                Nova consulta
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.7fr 1fr" }, gap: 3, alignItems: "start" }}>
            <Box sx={{ display: "grid", gap: 3 }}>
              <SectionCard title="Anamnese">{emptyText(patient.anamnese)}</SectionCard>
              <SectionCard title="Observações">{emptyText(patient.observation)}</SectionCard>
              <SectionCard title="Histórico de eventos">
                {(patient.events?.length ?? 0) > 0 ? <EventTimeline events={patient.events!} /> : emptyText(undefined)}
              </SectionCard>
            </Box>

            <Box sx={{ display: "grid", gap: 3 }}>
              <Box sx={{ background: theme.palette.brand.heroCard, border: `1px solid ${theme.palette.brand.heroCardBorder}`, borderRadius: "18px", p: 3, color: theme.palette.common.white }}>
                <Typography sx={{ mb: "14px", fontSize: "11px", fontWeight: 600, letterSpacing: "1.2px", textTransform: "uppercase", opacity: 0.8 }}>
                  Acompanhamento
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", pb: "13px", borderBottom: "1px solid rgba(255,255,255,0.16)" }}>
                  <Typography sx={{ fontSize: "13px", opacity: 0.8 }}>Última consulta</Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{formatDate(lastConsultationDate(patient))}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", pt: "13px" }}>
                  <Typography sx={{ fontSize: "13px", opacity: 0.8 }}>Próximo retorno</Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: theme.palette.brand.creamStroke === "transparent" ? "inherit" : theme.palette.brand.creamStroke }}>
                    {formatDate(patient.returnDate)}
                  </Typography>
                </Box>
              </Box>

              <SectionCard title="Galeria">
                {(patient.images?.length ?? 0) > 0 ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                    {patient.images!.map((img) => (
                      <Box key={img.id} component="img" src={img.imageUrl} alt="" sx={{ width: "100%", aspectRatio: "1", borderRadius: "11px", objectFit: "cover", border: `1px solid ${theme.palette.divider}` }} />
                    ))}
                  </Box>
                ) : (
                  emptyText(undefined)
                )}
              </SectionCard>
            </Box>
          </Box>

          {editOpen && (
            <PatientForm
              open={editOpen}
              patient={patient}
              allTags={allTags ?? patient.tags ?? []}
              onClose={() => setEditOpen(false)}
              onSave={handleSave}
              isLoading={saving}
            />
          )}

          <MessageDialog
            open={deleteOpen}
            message={
              <>
                Você deseja remover <strong>TODAS</strong> as informações do paciente, incluindo dados pessoais, anamnese, fotos e histórico de eventos? Esta ação não pode ser desfeita.
              </>
            }
            onConfirm={handleDelete}
            confirmButtonText="Sim"
            onClose={() => setDeleteOpen(false)}
            cancelButtonText="Cancelar"
          />
        </>
      )}
    </Box>
  );
};
